import { memo, useCallback, useEffect, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  createFilterOptions,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FileCell } from '../../grid/components/data-display/cells/FileCell';
import type {
  BankStatementImportCategoryOption,
  BankStatementImportCurrencyOption,
  BankStatementImportRelationOption,
  IncomeExpenseType,
} from '../types';
import type { ReviewedBankStatementTransaction } from '../reviewHelpers';
import { toDateInputValue } from '../../grid/utils/dateFormatters';
import { DraftTextField } from './DraftTextField';
import {
  ellipsisTextFieldSx,
  formatNumber,
  nullableNumber,
  valueOrUnresolved,
} from './bankStatementImportUiUtils';
import { validateTransaction } from '../validation';

type TransactionRowEditorProps = {
  row: ReviewedBankStatementTransaction;
  validationError?: string | null;
  index: number;
  invoiceOptions: BankStatementImportRelationOption[];
  onFileBusyChange: (rowId: string, busy: boolean) => void;
  categoryOptions: BankStatementImportCategoryOption[];
  counterpartyOptions: BankStatementImportRelationOption[];
  currencyOptions: BankStatementImportCurrencyOption[];
  disabled: boolean;
  onChange: (rowId: string, patch: Partial<ReviewedBankStatementTransaction>) => void;
  onDelete: (rowId: string) => void;
};

const filterRelationOptions = createFilterOptions<BankStatementImportRelationOption>({
  limit: 200,
  stringify: (option) => option.SearchText || option.Name,
});

export const TransactionRowEditor = memo(
  ({
    row,
    validationError,
    index,
    invoiceOptions,
    onFileBusyChange,
    categoryOptions,
    counterpartyOptions,
    currencyOptions,
    disabled,
    onChange,
    onDelete,
  }: TransactionRowEditorProps) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const errors = validateTransaction(row);
    const selectedInvoice =
      invoiceOptions.find((option) => option.Id === row.CrmInvoiceId) ??
      (row.CrmInvoiceId
        ? { Id: row.CrmInvoiceId, Name: row.InvoiceName || `Invoice #${row.CrmInvoiceId}` }
        : null);
    const handleFileBusyChange = useCallback(
      (busy: boolean) => onFileBusyChange(row.RowId, busy),
      [onFileBusyChange, row.RowId]
    );
    const selectedCategory =
      categoryOptions.find((option) => option.Id === row.CrmIncomeExpenseCategoryId) ?? null;
    const selectedCounterparty =
      counterpartyOptions.find((option) => option.Id === row.CounterpartyCrmRelationId) ??
      (row.CounterpartyCrmRelationId && row.CounterpartyName
        ? { Id: row.CounterpartyCrmRelationId, Name: row.CounterpartyName }
        : null);
    const selectedCurrency =
      currencyOptions.find((option) => option.Id === row.CrmCurrencyId) ??
      (row.CrmCurrencyId && row.Currency
        ? { Id: row.CrmCurrencyId, Code: row.Currency, DisplayName: row.Currency }
        : null);

    useEffect(() => {
      if (validationError) {
        rowRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    }, [validationError]);

    return (
      <Box
        ref={rowRef}
        data-transaction-row-id={row.RowId}
        data-transaction-error={validationError ? 'true' : undefined}
        sx={{
          border: 1,
          borderColor: validationError ? 'error.main' : 'divider',
          borderRadius: 1,
          p: 1.25,
          bgcolor: validationError ? 'rgba(211, 47, 47, 0.04)' : 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Transaction {index + 1}
          </Typography>
          <Box sx={{ textAlign: 'right', minWidth: 100 }}>
            <Typography variant="body2" fontWeight={600}>
              {formatNumber(row.Amount)} {row.Currency || ''}
            </Typography>
            {row.AmountEur != null && (
              <Typography variant="caption" color="text.secondary">
                ({formatNumber(row.AmountEur)} EUR)
              </Typography>
            )}
          </Box>
          <Tooltip title="Remove transaction">
            <span>
              <IconButton
                aria-label="Remove transaction"
                size="small"
                onClick={() => onDelete(row.RowId)}
                disabled={disabled}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {validationError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {validationError}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr 0.9fr 0.8fr 2fr' },
            gap: 1,
            alignItems: 'stretch',
            '& .MuiTextField-root': {
              minWidth: 0,
              '& .MuiInputBase-root': { minHeight: 40 },
              '& .MuiFormHelperText-root': {
                minHeight: '1.1em',
                mt: 0.25,
                lineHeight: 1.1,
                fontSize: '0.7rem',
              },
            },
          }}
        >
          <TextField
            required
            label="Date"
            type="date"
            size="small"
            value={toDateInputValue(row.TransactionDate)}
            onChange={(event) => onChange(row.RowId, { TransactionDate: event.target.value })}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            error={!!errors.date}
            helperText={errors.date || ' '}
            sx={{ gridColumn: { md: '1' }, gridRow: { md: '1' } }}
          />
          <TextField
            required
            select
            label="Type"
            size="small"
            value={row.IncomeExpenseType || ''}
            onChange={(event) =>
              onChange(row.RowId, {
                IncomeExpenseType: event.target.value as IncomeExpenseType,
                CrmIncomeExpenseCategoryId: null,
                CategoryName: null,
                BankCategoryRaw: null,
              })
            }
            disabled={disabled}
            error={!!errors.type}
            helperText={errors.type || ' '}
            sx={{ gridColumn: { md: '2' }, gridRow: { md: '1' } }}
          >
            <MenuItem value="Income">Income</MenuItem>
            <MenuItem value="Expense">Expense</MenuItem>
          </TextField>
          <DraftTextField
            required
            label="Amount"
            type="number"
            initialValue={
              row.Amount == null || !Number.isFinite(row.Amount) ? '' : String(row.Amount)
            }
            disabled={disabled}
            error={!!errors.amount}
            helperText={errors.amount || ' '}
            onCommit={(value) => onChange(row.RowId, { Amount: nullableNumber(value) })}
            sx={{ gridColumn: { md: '3' }, gridRow: { md: '1' } }}
          />
          <Autocomplete
            size="small"
            options={currencyOptions}
            value={selectedCurrency}
            getOptionLabel={(option) => option.DisplayName || option.Code}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            onChange={(_, selected) => {
              onChange(row.RowId, {
                CrmCurrencyId: selected?.Id ?? null,
                Currency: selected?.Code ?? null,
              });
            }}
            disabled={disabled}
            sx={{ gridColumn: { md: '4' }, gridRow: { md: '1' } }}
            renderInput={(params) => (
              <Tooltip
                title={selectedCurrency?.DisplayName || selectedCurrency?.Code || 'Unresolved'}
              >
                <TextField
                  {...params}
                  required
                  label="Currency"
                  error={!!errors.currency}
                  helperText={errors.currency || ' '}
                  sx={ellipsisTextFieldSx}
                />
              </Tooltip>
            )}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={counterpartyOptions}
            filterOptions={filterRelationOptions}
            value={selectedCounterparty}
            inputValue={row.CounterpartyName ?? row.CounterpartyRaw ?? ''}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.Name)}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            onChange={(_, value) => {
              const option = typeof value === 'string' ? null : value;
              const name = typeof value === 'string' ? value : value?.Name;
              onChange(row.RowId, {
                CounterpartyCrmRelationId: option?.Id ?? null,
                CounterpartyName: option?.Name ?? null,
                CounterpartyRaw: name ?? row.CounterpartyRaw,
              });
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'input' || reason === 'clear') {
                onChange(row.RowId, {
                  CounterpartyCrmRelationId: null,
                  CounterpartyName: null,
                  CounterpartyRaw: value,
                });
              }
            }}
            disabled={disabled}
            sx={{ gridColumn: { md: '1 / span 2' }, gridRow: { md: '2' } }}
            renderInput={(params) => (
              <Tooltip
                title={selectedCounterparty?.Name || row.CounterpartyRaw || 'Unresolved'}
                placement="top-start"
              >
                <TextField
                  {...params}
                  label="Client"
                  required
                  placeholder="Search/select..."
                  error={!!errors.client}
                  helperText={
                    errors.client ? (
                      errors.client
                    ) : (
                      <Tooltip title={row.CounterpartyRaw || 'Unresolved'} placement="top-start">
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.CounterpartyRaw ? `Bank: ${row.CounterpartyRaw}` : ' '}
                        </Box>
                      </Tooltip>
                    )
                  }
                  sx={ellipsisTextFieldSx}
                />
              </Tooltip>
            )}
          />
          <DraftTextField
            required
            label="Description"
            initialValue={row.TransactionDescription ?? ''}
            disabled={disabled}
            error={!!errors.description}
            helperText={errors.description || ' '}
            onCommit={(value) => onChange(row.RowId, { TransactionDescription: value })}
            sx={{ gridColumn: { md: '5' }, gridRow: { md: '1' } }}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={categoryOptions.filter(
              (option) => option.IncomeExpenseType === row.IncomeExpenseType
            )}
            value={selectedCategory}
            inputValue={row.CategoryName ?? row.BankCategoryRaw ?? ''}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.Name)}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            onChange={(_, value) => {
              const option = typeof value === 'string' ? null : value;
              const name = typeof value === 'string' ? value : value?.Name;
              onChange(row.RowId, {
                CrmIncomeExpenseCategoryId: option?.Id ?? null,
                CategoryName: option?.Name ?? null,
                BankCategoryRaw: name ?? row.BankCategoryRaw,
              });
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'input' || reason === 'clear') {
                onChange(row.RowId, {
                  CrmIncomeExpenseCategoryId: null,
                  CategoryName: null,
                  BankCategoryRaw: value,
                });
              }
            }}
            disabled={disabled}
            sx={{ gridColumn: { md: '3' }, gridRow: { md: '2' } }}
            renderInput={(params) => (
              <Tooltip
                title={selectedCategory?.Name || row.BankCategoryRaw || 'Unresolved'}
                placement="top-start"
              >
                <TextField
                  {...params}
                  label="Category"
                  required
                  placeholder="Select category..."
                  error={!!errors.category}
                  helperText={
                    errors.category ? (
                      errors.category
                    ) : (
                      <Tooltip title={row.BankCategoryRaw || 'Unresolved'} placement="top-start">
                        <Box
                          component="span"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Bank: {valueOrUnresolved(row.BankCategoryRaw)}
                        </Box>
                      </Tooltip>
                    )
                  }
                  sx={ellipsisTextFieldSx}
                />
              </Tooltip>
            )}
          />
          <DraftTextField
            label="Internal Description"
            initialValue={row.InternalDescription ?? ''}
            disabled={disabled}
            onCommit={(value) => onChange(row.RowId, { InternalDescription: value })}
            sx={{ gridColumn: { md: '4 / span 2' }, gridRow: { md: '2' } }}
          />
          <DraftTextField
            label="Exchange Rate"
            type="number"
            initialValue={row.ExchangeRate == null ? '' : String(row.ExchangeRate)}
            disabled={disabled}
            onCommit={(value) => onChange(row.RowId, { ExchangeRate: nullableNumber(value) })}
            sx={{ gridColumn: { md: '1' }, gridRow: { md: '3' } }}
          />
          <DraftTextField
            label="Amount EUR"
            type="number"
            initialValue={row.AmountEur == null ? '' : String(row.AmountEur)}
            disabled={disabled}
            onCommit={(value) => onChange(row.RowId, { AmountEur: nullableNumber(value) })}
            sx={{ gridColumn: { md: '2' }, gridRow: { md: '3' } }}
          />
          <DraftTextField
            label="Balance"
            type="number"
            initialValue={
              row.BalanceAfterTransaction == null ? '' : String(row.BalanceAfterTransaction)
            }
            disabled={disabled}
            onCommit={(value) =>
              onChange(row.RowId, { BalanceAfterTransaction: nullableNumber(value) })
            }
            sx={{ gridColumn: { md: '3' }, gridRow: { md: '3' } }}
          />
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(140px, 1fr)' },
            gap: 1,
            alignItems: 'start',
          }}
        >
          <Autocomplete
            size="small"
            options={invoiceOptions}
            value={selectedInvoice}
            filterOptions={filterRelationOptions}
            getOptionLabel={(option) => option.Name}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            onChange={(_, invoice) =>
              onChange(row.RowId, {
                CrmInvoiceId: invoice?.Id ?? null,
                InvoiceName: invoice?.Name ?? null,
              })
            }
            disabled={disabled}
            renderInput={(params) => (
              <TextField {...params} label="Invoice" placeholder="Search by number or ID..." />
            )}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Upload file
            </Typography>
            <FileCell
              fileGuid={row.InvoiceSnapshotFileId}
              isEditing
              disabled={disabled}
              onBusyChange={handleFileBusyChange}
              onValueChange={(fileId) => onChange(row.RowId, { InvoiceSnapshotFileId: fileId })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {row.InvoiceSnapshotFileId
                ? 'Your file will be attached and will not be replaced.'
                : row.CrmInvoiceId
                  ? 'A file will be generated from the selected invoice, or you can upload your own.'
                  : 'Optional. Select an invoice to generate a file, or upload your own file.'}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);
